import * as XLSX from 'xlsx';
import moment from 'moment';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';

import { roundTotalRuntime } from '../helpers/time';
import { formatTaskForExport, getViewColumnToExportMap } from './timecard.helpers';

const formatDate = (timestamp, _, task) => {
  const ourDate = TaskHelpers.getTaskDate(task ?? {});
  if (ourDate) {
    return moment(
      ourDate.toMillis(),
    ).format('MMM Do YYYY');
  }
  if (!timestamp) return '';
  return moment(timestamp).format('MMM Do YYYY');
};
const formatTime = (timestamp, zone) => {
  if (!timestamp) return '';
  return DateTime.fromMillis(timestamp, { zone })
    .toLocaleString({
      hour: '2-digit',
      minute: 'numeric',
      timeZoneName: 'short',
    });
};

const formatRuntime = (duration) => {
  const milliseconds = parseInt((duration % 1000) / 100, 10);
  let seconds = Math.floor((duration / 1000) % 60);
  let minutes = Math.floor((duration / (1000 * 60)) % 60);
  let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

  hours = (hours < 10) ? `0${hours}` : hours;
  minutes = (minutes < 10) ? `0${minutes}` : minutes;
  seconds = (seconds < 10) ? `0${seconds}` : seconds;

  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const createDayRow = ({
  dayRows = [],
  lastDay,
  timeSummary,
  roundingInterval,
  firstWrite,
  roundingType,
  roundingSetting,
}) => {
  let rows = [];
  const runtime = formatRuntime(
    roundTotalRuntime(timeSummary, roundingInterval, roundingType, roundingSetting),
  );
  const [hours, minutes] = runtime.split(':');
  if (!firstWrite) rows.push([]);
  rows.push([`SUMMARY: ${lastDay.toUpperCase()}`, '', '', '', '', `${hours}:${minutes}`]);
  rows = rows.concat(dayRows);
  return rows;
};

const getBaseExcelHeader = (t) => [
  'DATE',
  'NAME',
  'REGULAR START TIME',
  'REGULAR END TIME',
  'BREAK START TIME',
  'BREAK END TIME',
  'OT START TIME',
  'OT END TIME',
  'DT OT START TIME',
  'DT OT END TIME',
  'DURATION',
  'REGULAR DURATION',
  'BREAK DURATION',
  'OT DURATION',
  'DT OT DURATION',
  'PTO DURATION',
  'TYPE',
  `${t('Project').toUpperCase()} NAME`,
  'PHASE',
  'COST CODE',
  'UNION',
  'LOCAL CLASS',
  'WORK CLASS',
  'NOTES',
  'ENTERED VIA',
  'APPROVED BY',
  'VIOLATION',
];

export default ({
  t,
  user,
  tasks,
  start,
  end,
  regularHours,
  otHours,
  doubleOtHours,
  saturdayHours,
  saturdayOTHours,
  saturdayDoubleOTHours,
  sundayHours,
  sundayOTHours,
  sundayDoubleOTHours,
  ptoHours,
  totalPay,
  roundingInterval,
  roundingType,
  roundingSetting,
  isSummary,
  userMap = {},
  showOriginalTimes,
  mainColumns,
  customColumns,
  unionMap = {},
  unionLocalMap = {},
  classMap = {},
  phaseMap = {},
  customFieldMap = {},
  divisionIds = new Set(),
}) => {
  const colMap = getViewColumnToExportMap(t);

  const includeAllColumns = !mainColumns;

  const mainColSet = new Set(
    (mainColumns ?? []).map((col) => colMap[col]),
  );

  const headerRow = getBaseExcelHeader(t).filter((col) => (
    includeAllColumns || mainColSet.has(col)
  ));

  customColumns?.forEach((fieldId) => {
    const customField = customFieldMap[fieldId];

    if (customField && divisionIds.has(customField.divisionId)) {
      if (customField.columns?.length) {
        customField.columns?.forEach((col) => {
          headerRow.push(`${customField.title} - ${col.name}`.toUpperCase());
        });
      } else {
        headerRow.push(customField.title.toUpperCase());
      }
    }
  });

  const hasStatus = mainColSet.has('STATUS');

  if (hasStatus) {
    headerRow.push('STATUS');
  }

  const headerSet = new Set(headerRow);

  const hasPTO = (ptoHours ?? 0) > 0;

  const title = `${user} ${formatDate(start)} - ${formatDate(end)} Timecard Breakdown`;
  const workbook = XLSX.utils.book_new();
  const showSaturday = !isSummary && (saturdayHours || saturdayOTHours || saturdayDoubleOTHours);
  const showSunday = !isSummary && (sundayHours || sundayOTHours || sundayDoubleOTHours);
  const firstRow = ['Employee:', user, '', formatDate(start), '-', formatDate(end)];
  if (!isSummary) {
    const headerRow = ['REGULAR:', 'OVERTIME:', 'DOUBLE OVERTIME:'];
    if (hasPTO) {
      headerRow.push('PTO:');
    }
    headerRow.push('TOTAL PAY:');
    firstRow.push(...headerRow);
  }
  const hoursRow = ['', '', '', '', '', '', regularHours, otHours, doubleOtHours];
  if (hasPTO) {
    hoursRow.push(ptoHours);
  }
  hoursRow.push(totalPay);
  let rows = [
    firstRow,
    !isSummary && hoursRow,
    showSaturday && ['', '', '', '', '', '', 'SATURDAY HOURS:', 'SATURDAY OT HOURS:', 'SATURDAY DT OT HOURS:'],
    showSaturday && ['', '', '', '', '', '', saturdayHours, saturdayOTHours, saturdayDoubleOTHours],
    showSunday && ['', '', '', '', '', '', 'SUNDAY HOURS:', 'SUNDAY OT HOURS:', 'SUNDAY DT OT HOURS:'],
    showSunday && ['', '', '', '', '', '', sundayHours, sundayOTHours, sundayDoubleOTHours],
    [],
    headerRow,
  ].filter(Boolean);

  let lastDay = null;
  let firstWrite = true;
  let dayRows = [];
  let timeSummary = 0;
  let lastUser = null;
  tasks.forEach((task, idx) => {
    const { userId } = task;

    const timestamp = TaskHelpers.getEndTime(task) ?? TaskHelpers.getStartTime(task);
    const runtimes = TaskHelpers.getRuntimes(task);
    const totalRuntime = runtimes.regularTime + runtimes.overtime + runtimes.doubleOT;
    const dayKey = formatDate(timestamp, task.timezone, task);

    if (dayKey !== lastDay || userId !== lastUser) {
      if (dayRows.length > 0) {
        rows = rows.concat(createDayRow({
          dayRows,
          lastDay,
          timeSummary,
          roundingInterval: roundingInterval || 1,
          firstWrite,
          roundingType,
          roundingSetting,
        }));
        firstWrite = false;
      }
      lastUser = userId;
      lastDay = dayKey;
      dayRows = [];
      timeSummary = 0;
    }

    const row = formatTaskForExport({
      t,
      showOriginalTimes,
      task,
      phaseMap,
      classMap,
      unionLocalMap,
      unionMap,
      userMap,
      headerSet,
      customColumns,
      divisionIds,
      customFieldMap,
      exportType: 'excel',
      timeFormatter: formatTime,
      dateFormatter: formatDate,
    });

    dayRows.push(row);

    timeSummary += totalRuntime;
    if (idx === tasks.length - 1 && dayRows.length > 0) {
      rows = rows.concat(createDayRow({
        dayRows,
        lastDay,
        timeSummary,
        roundingInterval: roundingInterval || 1,
        firstWrite,
        roundingType,
        roundingSetting,
      }));
    }
  });

  rows.push([]);

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  // Sheet name cannot exceed 31 char.
  XLSX.utils.book_append_sheet(workbook, sheet, title.slice(0, 31));
  XLSX.writeFile(workbook, `${title}.xlsx`);
};
